/** 시설별 웨어러블 BLE/게이트웨이 — 프로필 + 파라미터 (백엔드 pool_wearable_gateway_config.py 와 동기) */

export type WearableGatewayProfileId =
  | 'disabled'
  | 'kyma_websocket_direct'
  | 'mqtt_v1'
  | 'http_webhook_v1'
  | 'ble_mqtt_bridge_v1';

export type WearableGatewayFieldType = 'string' | 'number' | 'boolean' | 'password' | 'text';

export interface WearableGatewayFieldDef {
  key: string;
  label: string;
  type: WearableGatewayFieldType;
  required?: boolean;
  default?: string | number | boolean;
  helper?: string;
}

export interface WearableGatewayProfileMeta {
  id: WearableGatewayProfileId;
  label: string;
  description: string;
  adapter_module: string;
  fields: WearableGatewayFieldDef[];
}

export interface PoolWearableGatewayConfig {
  profile_id: WearableGatewayProfileId;
  enabled: boolean;
  parameters: Record<string, string | number | boolean>;
  notes: string;
  /** API 조회 시에만 포함 */
  pool_id?: number;
  pool_code?: string;
  adapter_module?: string;
}

/** UI·문서용 추천 시작 프로필 */
export const RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID: WearableGatewayProfileId = 'mqtt_v1';

const SAMPLE_POOL_CODE = 'POOL001';

function withPoolPlaceholders(
  value: string,
  poolCode: string,
  poolId?: number | string
): string {
  return value
    .replace(/\{pool_code\}/g, poolCode)
    .replace(/\{pool_id\}/g, String(poolId ?? ''))
    .replace(/\{device_id\}/g, 'WB-SAMPLE-001');
}

function cloneSampleParameters(
  params: Record<string, string | number | boolean>,
  poolCode: string,
  poolId?: number | string
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') {
      out[k] = withPoolPlaceholders(v, poolCode, poolId);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** 프로필별 샘플 (현장에 맞게 호스트·비밀번호만 바꿔 쓰는 용도) */
export const WEARABLE_GATEWAY_PROFILE_SAMPLES: Record<
  Exclude<WearableGatewayProfileId, 'disabled'>,
  Omit<PoolWearableGatewayConfig, 'pool_id' | 'pool_code' | 'adapter_module'>
> = {
  kyma_websocket_direct: {
    profile_id: 'kyma_websocket_direct',
    enabled: true,
    parameters: {
      notes:
        '안전요원 단말 앱이 wss://{서버}/wearable/{device_id} 로 연결. device_id는 웨어러블 관리에 등록한 ID와 동일해야.',
    },
    notes: '[샘플] WebSocket 직결 — 별도 게이트웨이 없이 앱 알림만 사용할 때',
  },
  mqtt_v1: {
    profile_id: 'mqtt_v1',
    enabled: true,
    parameters: {
      mqtt_host: 'mqtt.example.local',
      mqtt_port: 1883,
      mqtt_tls: false,
      mqtt_username: 'pool_gateway',
      mqtt_password: 'sample-mqtt-password',
      ingress_bearer_token: 'sample-ingress-token-change-me',
      topic_telemetry_template: 'pool/{pool_code}/wearable/{device_id}/telemetry',
      topic_command_template: 'pool/{pool_code}/wearable/{device_id}/command',
      manufacturer_code: 'vendor_demo_mqtt_v1',
    },
    notes:
      '[샘플] MQTT — 브로커 주소·계정·토큰을 현장 값으로 바꾼 뒤 저장. HTTP 텔레메트리: POST /api/pools/{pool_id}/wearable-gateway/telemetry',
  },
  http_webhook_v1: {
    profile_id: 'http_webhook_v1',
    enabled: true,
    parameters: {
      telemetry_ingress_url: 'https://api.example.com/v1/wearable/telemetry',
      command_egress_url: 'https://api.example.com/v1/wearable/command',
      auth_header_name: 'Authorization',
      auth_header_value: 'Bearer sample-vendor-token',
      device_id_field: 'device_id',
      manufacturer_code: 'vendor_demo_http_v1',
      ingress_bearer_token: 'sample-ingress-token-change-me',
    },
    notes:
      '[샘플] HTTP — 제조사가 정한 URL·헤더를 넣고, KYMA 수신은 동일 토큰으로 POST /api/pools/{pool_id}/wearable-gateway/telemetry',
  },
  ble_mqtt_bridge_v1: {
    profile_id: 'ble_mqtt_bridge_v1',
    enabled: true,
    parameters: {
      gateway_device_id: 'GW-{pool_code}-001',
      manufacturer_code: 'vendor_demo_ble_hub',
      ble_scan_service_uuid: '0000180d-0000-1000-8000-00805f9b34fb',
      mqtt_host: 'mqtt.example.local',
      mqtt_port: 1883,
      mqtt_tls: false,
      mqtt_username: 'ble_bridge',
      mqtt_password: 'sample-mqtt-password',
      topic_prefix: 'pool/{pool_code}/gateway',
      topic_telemetry_template: 'pool/{pool_code}/wearable/{device_id}/telemetry',
      ingress_bearer_token: 'sample-ingress-token-change-me',
    },
    notes: '[샘플] BLE 허브 + MQTT — 현장 게이트웨이 ID·BLE UUID·브로커 정보를 제조사 문서에 맞게 수정',
  },
};

export function getWearableGatewaySampleConfig(
  profileId: WearableGatewayProfileId,
  options?: { poolCode?: string; poolId?: number | string }
): PoolWearableGatewayConfig | null {
  if (profileId === 'disabled') {
    return null;
  }
  const poolCode = options?.poolCode?.trim() || SAMPLE_POOL_CODE;
  const poolId = options?.poolId;
  const base = WEARABLE_GATEWAY_PROFILE_SAMPLES[profileId];
  return {
    profile_id: base.profile_id,
    enabled: base.enabled,
    notes: withPoolPlaceholders(base.notes, poolCode, poolId),
    parameters: cloneSampleParameters(base.parameters, poolCode, poolId),
  };
}

export function getRecommendedWearableGatewaySample(
  options?: { poolCode?: string; poolId?: number | string }
): PoolWearableGatewayConfig {
  return getWearableGatewaySampleConfig(RECOMMENDED_WEARABLE_GATEWAY_PROFILE_ID, options)!;
}

/** 저장된 값이 비어 있어 샘플 안내를 기본 노출할지 */
export function isWearableGatewayConfigUnset(
  cfg: PoolWearableGatewayConfig | null | undefined
): boolean {
  if (!cfg) return true;
  if (cfg.profile_id !== 'disabled' && cfg.enabled) return false;
  if (cfg.profile_id !== 'disabled') {
    const hasParam = Object.values(cfg.parameters || {}).some((v) => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      return String(v).trim() !== '';
    });
    if (hasParam) return false;
  }
  return !String(cfg.notes || '').trim();
}

export const DEFAULT_POOL_WEARABLE_GATEWAY_CONFIG: PoolWearableGatewayConfig = {
  profile_id: 'disabled',
  enabled: false,
  parameters: {},
  notes: '',
};

/** API 실패 시 UI용 (백엔드 pool_wearable_gateway_config.py 와 동기) */
export const WEARABLE_GATEWAY_PROFILES_FALLBACK: WearableGatewayProfileMeta[] = [
  {
    id: 'disabled',
    label: '미사용 (게이트웨이 없음)',
    description: '시설별 웨어러블 게이트웨이 연동을 쓰지 않음',
    adapter_module: 'none',
    fields: [],
  },
  {
    id: 'kyma_websocket_direct',
    label: 'KYMA WebSocket 직결 (단말 앱)',
    description: '밴드 대신 단말 앱이 서버 WebSocket으로 직접 연결할 때.',
    adapter_module: 'adapters.kyma_websocket',
    fields: [
      {
        key: 'notes',
        label: '비고',
        type: 'text',
        helper: '단말이 KYMA /wearable/{device_id} WebSocket에 직접 연결하는 경우',
      },
    ],
  },
  {
    id: 'mqtt_v1',
    label: 'MQTT 게이트웨이',
    description: '제조사 게이트웨이가 MQTT로 텔레메트리·명령을 주고받을 때.',
    adapter_module: 'adapters.mqtt_gateway',
    fields: [
      { key: 'mqtt_host', label: 'MQTT 호스트', type: 'string', required: true },
      { key: 'mqtt_port', label: 'MQTT 포트', type: 'number', required: true, default: 1883 },
      { key: 'mqtt_tls', label: 'TLS 사용', type: 'boolean', default: true },
      { key: 'mqtt_username', label: 'MQTT 사용자', type: 'string' },
      { key: 'mqtt_password', label: 'MQTT 비밀번호', type: 'password' },
      {
        key: 'ingress_bearer_token',
        label: '서버 HTTP 수신 토큰',
        type: 'password',
        helper: 'POST /api/pools/{id}/wearable-gateway/telemetry',
      },
      {
        key: 'topic_telemetry_template',
        label: '텔레메트리 토픽 템플릿',
        type: 'string',
        required: true,
        default: 'pool/{pool_code}/wearable/{device_id}/telemetry',
      },
      {
        key: 'topic_command_template',
        label: '명령 토픽 템플릿',
        type: 'string',
        required: true,
        default: 'pool/{pool_code}/wearable/{device_id}/command',
      },
      { key: 'manufacturer_code', label: '제조사/모델 코드', type: 'string' },
    ],
  },
  {
    id: 'http_webhook_v1',
    label: 'HTTP Webhook 게이트웨이',
    description: '게이트웨이가 HTTP로 데이터를 밀어 넣거나 명령을 받을 때.',
    adapter_module: 'adapters.http_webhook',
    fields: [
      { key: 'telemetry_ingress_url', label: '생체 업링크 URL', type: 'string', required: true },
      { key: 'command_egress_url', label: '알림/명령 URL', type: 'string' },
      { key: 'auth_header_name', label: '인증 헤더 이름', type: 'string', default: 'Authorization' },
      { key: 'auth_header_value', label: '인증 헤더 값', type: 'password' },
      { key: 'device_id_field', label: 'device_id JSON 필드명', type: 'string', default: 'device_id' },
      { key: 'manufacturer_code', label: '제조사/모델 코드', type: 'string' },
      { key: 'ingress_bearer_token', label: '서버 HTTP 수신 토큰', type: 'password' },
    ],
  },
  {
    id: 'ble_mqtt_bridge_v1',
    label: 'BLE → MQTT 브릿지',
    description: '현장 BLE 허브가 MQTT로 중앙과 통신할 때 (일반적인 BLE 밴드 구성).',
    adapter_module: 'adapters.ble_mqtt_bridge',
    fields: [
      { key: 'gateway_device_id', label: '현장 게이트웨이 ID', type: 'string', required: true },
      { key: 'manufacturer_code', label: '제조사/모델 코드', type: 'string', required: true },
      { key: 'ble_scan_service_uuid', label: 'BLE 서비스 UUID', type: 'string' },
      { key: 'mqtt_host', label: 'MQTT 호스트', type: 'string', required: true },
      { key: 'mqtt_port', label: 'MQTT 포트', type: 'number', required: true, default: 1883 },
      { key: 'mqtt_tls', label: 'TLS 사용', type: 'boolean', default: true },
      { key: 'mqtt_username', label: 'MQTT 사용자', type: 'string' },
      { key: 'mqtt_password', label: 'MQTT 비밀번호', type: 'password' },
      {
        key: 'topic_prefix',
        label: 'MQTT 토픽 prefix',
        type: 'string',
        required: true,
        default: 'pool/{pool_code}/gateway',
      },
      {
        key: 'topic_telemetry_template',
        label: '텔레메트리 토픽 템플릿',
        type: 'string',
        default: 'pool/{pool_code}/wearable/{device_id}/telemetry',
      },
      { key: 'ingress_bearer_token', label: '서버 HTTP 수신 토큰', type: 'password' },
    ],
  },
];

export function resolveWearableGatewayProfiles(
  apiProfiles: WearableGatewayProfileMeta[] | null | undefined
): WearableGatewayProfileMeta[] {
  if (apiProfiles && apiProfiles.length > 0) {
    return apiProfiles;
  }
  return WEARABLE_GATEWAY_PROFILES_FALLBACK;
}

export function mergePoolWearableGatewayConfig(
  raw: Partial<PoolWearableGatewayConfig> | null | undefined,
  profileFields?: WearableGatewayFieldDef[]
): PoolWearableGatewayConfig {
  const base = { ...DEFAULT_POOL_WEARABLE_GATEWAY_CONFIG, ...(raw || {}) };
  const pid = (base.profile_id || 'disabled') as WearableGatewayProfileId;
  const params: Record<string, string | number | boolean> = {};
  const fields = profileFields || [];
  for (const f of fields) {
    const fromRaw = raw?.parameters?.[f.key];
    if (fromRaw !== undefined && fromRaw !== null) {
      params[f.key] = fromRaw as string | number | boolean;
    } else if (f.default !== undefined) {
      params[f.key] = f.default;
    } else if (f.type === 'boolean') {
      params[f.key] = false;
    } else if (f.type === 'number') {
      params[f.key] = 0;
    } else {
      params[f.key] = '';
    }
  }
  if (raw?.parameters) {
    for (const [k, v] of Object.entries(raw.parameters)) {
      if (!(k in params)) {
        params[k] = v as string | number | boolean;
      }
    }
  }
  return {
    profile_id: pid,
    enabled: Boolean(base.enabled) && pid !== 'disabled',
    parameters: params,
    notes: String(base.notes || '').trim(),
  };
}
